import {
  personLabel,
  UNNAMED_PERSON_GLYPH,
  type HouseholdState,
} from '@foerier/shared'

/** A Person, and the label every surface draws them by. */
export interface PersonRow {
  id: string
  /** `personLabel` — the recorded name, or `—` when there is none. */
  label: string
}

/**
 * Every recorded Person, alphabetically.
 *
 * **By the label a row actually draws**, not by the stored name: a Person
 * whose name is absent or cleared draws `—`, and sorting by the raw name
 * would file them by an empty string while the reader sees a dash. The id
 * breaks the tie so the order is **total** — two devices with identical
 * state must draw the same list, the same rule `dimensionValues` and the
 * group headers already follow.
 *
 * Lives here rather than in either caller because the People screen and the
 * owner picker are two views of one list; if they sorted differently, picking
 * "the third one down" would mean two different People.
 */
export function sortedPeople(state: HouseholdState): readonly PersonRow[] {
  return Object.values(state.people)
    .map((person) => ({ id: person.id, label: personLabel(state, person.id) }))
    .sort((a, b) => {
      const al = a.label.toLowerCase()
      const bl = b.label.toLowerCase()
      if (al !== bl) return al < bl ? -1 : 1
      if (a.id === b.id) return 0
      return a.id < b.id ? -1 : 1
    })
}

/**
 * A `PersonCircle`'s letter: the label's first character, upper-cased, or
 * `undefined` for the sentinel — which draws an **empty** circle rather than
 * a placeholder letter, because a Person with no folded name is a fact the
 * app does not have and inventing one would be worse than blank.
 *
 * **Shared rather than repeated, at the third copy.** `EntryRow`,
 * `PackingRow` and F4's own person headers each carried this verbatim, each
 * with a docstring admitting it was copied; the argument that a helper would
 * be a longer name than the line it replaces stops paying once three call
 * sites have to agree about the sentinel. It lives beside {@link sortedPeople}
 * for that function's reason: this is *how a screen draws a Person*, and the
 * screens that draw one must not disagree.
 *
 * The seven surfaces that hand-roll `label.charAt(0).toUpperCase()` for a
 * name they already hold (`Trip`, `TripCard`, `NewTrip`, `People`,
 * `PiecePicker`, `GearListBuilder`, `ParticipantPicker`) are asking a
 * different question — a Person's initial, never a sentinel's absence — and
 * are deliberately left alone.
 */
export function personInitial(label: string): string | undefined {
  return label === UNNAMED_PERSON_GLYPH
    ? undefined
    : label.charAt(0).toUpperCase()
}
